import * as React from 'react';
import { Plus, Trash2, Volume2 } from 'lucide-react';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Input';
import type { ActivityFormData } from '../ActivityEditorPage.types';
import styles from './TypeSpecificFields.module.scss';

interface AudioOrderingFieldsProps {
  audioText: string;
  audioUrl?: string | null;
  words: string[];
  correctOrder: number[];
  onChange: (updates: Partial<ActivityFormData>) => void;
  onGenerateAudio?: () => Promise<string>;
  errors?: {
    audioText?: string;
    words?: string;
  };
}

export function AudioOrderingFields({
  audioText,
  audioUrl,
  words,
  correctOrder,
  onChange,
  onGenerateAudio,
  errors,
}: AudioOrderingFieldsProps) {
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [generateError, setGenerateError] = React.useState<string | null>(null);

  const handleGenerateAudio = async () => {
    if (!onGenerateAudio) return;
    setIsGenerating(true);
    setGenerateError(null);
    try {
      const url = await onGenerateAudio();
      onChange({ audioUrl: url });
    } catch {
      setGenerateError('Error al generar el audio. Inténtalo de nuevo.');
    } finally {
      setIsGenerating(false);
    }
  };
  const handleWordChange = (index: number, value: string) => {
    const newWords = [...words];
    newWords[index] = value;
    onChange({ words: newWords });
  };

  const handleAddWord = () => {
    const newWords = [...words, ''];
    const newOrder = [...correctOrder, newWords.length - 1];
    onChange({ words: newWords, correctOrder: newOrder });
  };

  const handleRemoveWord = (index: number) => {
    if (words.length <= 2) return;
    const newWords = words.filter((_, i) => i !== index);
    const newOrder = correctOrder
      .filter((o) => o !== index)
      .map((o) => (o > index ? o - 1 : o));
    onChange({ words: newWords, correctOrder: newOrder });
  };

  return (
    <div className={styles.typeFields}>
      <h3 className={styles.typeFields__title}>Escuchar audio y ordenar frase</h3>
      <p className={styles.typeFields__description}>
        La IA genera un clip de audio con la frase completa. El alumno escucha y ordena las palabras con tap.
        Define las palabras en el <strong>orden correcto</strong> — se barajarán automáticamente al mostrarse.
      </p>

      <div>
        <label htmlFor="audioTextOrdering" className={styles.typeFields__title}>
          Frase completa (texto del audio)
        </label>
        <Input
          id="audioTextOrdering"
          value={audioText}
          onChange={(e) => onChange({ audioText: e.target.value })}
          placeholder="Ej: She goes to the market every Sunday."
        />
        <p className={styles.typeFields__hint}>
          Debe ser una frase corta del script de la historia (4–8 palabras).
        </p>
        {errors?.audioText && (
          <span className={styles.typeFields__error}>{errors.audioText}</span>
        )}
      </div>

      <div className={styles.typeFields__audioRow}>
        {audioUrl ? (
          <div className={styles.typeFields__audioStatus}>
            <span className={styles.typeFields__audioUrl} title={audioUrl}>
              Audio: {audioUrl.split('/').pop()}
            </span>
            {onGenerateAudio && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                leftIcon={<Volume2 />}
                onClick={handleGenerateAudio}
                disabled={isGenerating || !audioText.trim()}
              >
                {isGenerating ? 'Generando...' : 'Regenerar audio'}
              </Button>
            )}
          </div>
        ) : onGenerateAudio ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            leftIcon={<Volume2 />}
            onClick={handleGenerateAudio}
            disabled={isGenerating || !audioText.trim()}
          >
            {isGenerating ? 'Generando...' : 'Generar audio'}
          </Button>
        ) : (
          <p className={styles.typeFields__hint}>
            Guarda la actividad primero para poder generar el audio.
          </p>
        )}
        {generateError && (
          <span className={styles.typeFields__error}>{generateError}</span>
        )}
      </div>

      <div>
        <label className={styles.typeFields__title}>
          Palabras en orden correcto ({words.length})
        </label>
        <p className={styles.typeFields__description}>
          Introduce cada palabra de la frase. El índice de posición se calcula automáticamente.
        </p>
        <div className={styles.typeFields__list}>
          {words.map((word, index) => (
            <div key={index} className={styles.typeFields__optionItem}>
              <span style={{ minWidth: '24px', textAlign: 'center', color: '#6B7280' }}>
                {index + 1}.
              </span>
              <div className={styles.typeFields__optionInput}>
                <Input
                  value={word}
                  onChange={(e) => handleWordChange(index, e.target.value)}
                  placeholder={`Palabra ${index + 1}`}
                />
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                leftIcon={<Trash2 />}
                onClick={() => handleRemoveWord(index)}
                disabled={words.length <= 2}
                aria-label={`Eliminar palabra ${index + 1}`}
              >
                <span className="sr-only">Eliminar</span>
              </Button>
            </div>
          ))}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          leftIcon={<Plus />}
          onClick={handleAddWord}
          className={styles.typeFields__addButton}
        >
          Añadir palabra
        </Button>
        {errors?.words && <span className={styles.typeFields__error}>{errors.words}</span>}
      </div>
    </div>
  );
}
